import { useMemo, useState } from "react";

import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell, LayoutGrid, List, PencilRuler, Salad, Search, Trash2 } from "lucide-react";

type CatalogDiet = {
  id: string;
  title: string;
  description: string;
  coverImageUrl?: string;
  waterTargetLiters?: number;
  mealType?: string;
  active: boolean;
  tags: string[];
};

type CatalogResponse = {
  ok: true;
  packages: unknown[];
  products: unknown[];
  meditations: unknown[];
  diets: CatalogDiet[];
  templates: unknown[];
  settings: unknown;
};

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveDiet(payload: Partial<CatalogDiet> & { title: string }) {
  const res = await adminFetch("/api/admin/numeroloji/diets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Diyet kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogDiet };
}

async function deleteDiet(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/diets/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Diyet silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

export default function AdminNumerolojiDiets() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 0,
  });

  const diets = useMemo(() => catalogQuery.data?.diets ?? [], [catalogQuery.data]);

  const dietSaveMutation = useMutation({
    mutationFn: (payload: any) => saveDiet(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const dietDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteDiet(id),
    onSuccess: async (_result, deletedId) => {
      queryClient.setQueryData<CatalogResponse | undefined>(["admin-numeroloji-catalog"], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          diets: (prev.diets ?? []).filter((diet) => diet.id !== deletedId),
        };
      });

      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const [dietSearch, setDietSearch] = useState("");
  const [dietFilter, setDietFilter] = useState<"all" | "active" | "inactive">("all");
  const [dietSort, setDietSort] = useState<"newest" | "title-asc" | "water-asc" | "water-desc">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const visibleDiets = useMemo(() => {
    const q = dietSearch.trim().toLowerCase();

    let rows = diets;

    if (q) {
      rows = rows.filter((d) => {
        const hay = `${d.title} ${d.description} ${d.mealType ?? ""} ${(d.tags ?? []).join(" ")}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (dietFilter === "active") {
      rows = rows.filter((d) => d.active);
    } else if (dietFilter === "inactive") {
      rows = rows.filter((d) => !d.active);
    }

    const sorted = [...rows];
    if (dietSort === "title-asc") {
      sorted.sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "tr"));
    } else if (dietSort === "water-asc") {
      sorted.sort((a, b) => Number(a.waterTargetLiters ?? 0) - Number(b.waterTargetLiters ?? 0));
    } else if (dietSort === "water-desc") {
      sorted.sort((a, b) => Number(b.waterTargetLiters ?? 0) - Number(a.waterTargetLiters ?? 0));
    }
    // "newest" = server order (created_at DESC from hydration)

    return sorted;
  }, [dietFilter, dietSearch, dietSort, diets]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                Diyet Yönetimi
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Diyetler</div>
                  <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                    Diyetleri etiketleyerek kullanıcıya özel gösterebilirsiniz.
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aktif</div>
                      <div className="text-xl font-extrabold">{diets.filter((d) => d.active).length}</div>
                    </div>
                  </div>

                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                      <div className="h-2 w-2 rounded-full bg-slate-400" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Pasif</div>
                      <div className="text-xl font-extrabold">{diets.filter((d) => !d.active).length}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                    placeholder="Başlık, etiket, öğün tipi..."
                    value={dietSearch}
                    onChange={(e) => setDietSearch(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={dietFilter === "all" ? "default" : "outline"}
                    className={cn("rounded-full", dietFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setDietFilter("all")}
                  >
                    Tümü
                  </Button>
                  <Button
                    size="sm"
                    variant={dietFilter === "active" ? "default" : "outline"}
                    className={cn("rounded-full", dietFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setDietFilter("active")}
                  >
                    Aktif
                  </Button>
                  <Button
                    size="sm"
                    variant={dietFilter === "inactive" ? "default" : "outline"}
                    className={cn("rounded-full", dietFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setDietFilter("inactive")}
                  >
                    Pasif
                  </Button>

                  <div className="ml-0 flex items-center gap-2 lg:ml-2">
                    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                      value={dietSort}
                      onChange={(e) => setDietSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">Başlık (A-Z)</option>
                      <option value="water-asc">Su hedefi (Artan)</option>
                      <option value="water-desc">Su hedefi (Azalan)</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-950/40">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>

                  <Button size="sm" className="rounded-full" onClick={() => navigate("/admin/numeroloji/diyet/yeni")}>
                    <span className="mr-1">+</span>
                    Yeni diyet
                  </Button>
                </div>
              </div>

              {dietSaveMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(dietSaveMutation.error as Error).message}
                </div>
              ) : null}

              {dietDeleteMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(dietDeleteMutation.error as Error).message}
                </div>
              ) : null}

              {viewMode === "grid" ? (
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visibleDiets.map((d) => {
                    const badge = d.active ? "Aktif" : "Pasif";
                    const badgeClass = d.active ? "bg-emerald-500" : "bg-slate-500";

                    return (
                      <div
                        key={d.id}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className="relative aspect-[4/3] bg-gradient-to-br from-emerald-500/15 via-blue-600/10 to-slate-950/20">
                          {d.coverImageUrl ? (
                            <img src={d.coverImageUrl} alt={d.title} className="absolute inset-0 h-full w-full object-cover" />
                          ) : null}

                          <div
                            className={cn(
                              "absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white",
                              badgeClass,
                            )}
                          >
                            {badge}
                          </div>

                          <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                              onClick={() => navigate(`/admin/numeroloji/diyet/${encodeURIComponent(d.id)}`)}
                            >
                              <PencilRuler className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                              disabled={dietDeleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu diyeti silmek istiyor musunuz?");
                                if (!ok) return;
                                dietDeleteMutation.mutate(d.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
                            {d.waterTargetLiters !== undefined ? (
                              <div className="rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                                {d.waterTargetLiters} L
                              </div>
                            ) : null}
                            {d.mealType ? (
                              <div className="rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                                {d.mealType}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-base font-bold text-slate-900 dark:text-white">{d.title}</div>
                            <Salad className="h-4 w-4 text-emerald-600/70 dark:text-emerald-300/80" />
                          </div>

                          <div className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">{d.description}</div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="flex flex-wrap gap-1">
                              {(d.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span
                                  key={`${d.id}-tag-${idx}`}
                                  className="rounded-full bg-blue-600/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>

                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={d.active}
                                disabled={dietSaveMutation.isPending}
                                onChange={() =>
                                  dietSaveMutation.mutate({
                                    ...d,
                                    active: !d.active,
                                  })
                                }
                              />
                              <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate("/admin/numeroloji/diyet/yeni")}
                    className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-2xl">+</span>
                    </div>
                    <div className="mt-3 text-sm font-bold">Yeni diyet ekle</div>
                    <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                      Yeni bir diyet planı oluştur.
                    </div>
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visibleDiets.map((d) => {
                    const badge = d.active ? "Aktif" : "Pasif";
                    const badgeClass = d.active ? "bg-emerald-500" : "bg-slate-500";

                    return (
                      <div
                        key={d.id}
                        className="group flex overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className="relative w-24 flex-none bg-gradient-to-br from-emerald-500/15 via-blue-600/10 to-slate-950/20 sm:w-36">
                          {d.coverImageUrl ? (
                            <img src={d.coverImageUrl} alt={d.title} className="absolute inset-0 h-full w-full object-cover" />
                          ) : null}
                          <div className={cn("absolute left-2 top-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white", badgeClass)}>
                            {badge}
                          </div>
                        </div>

                        <div className="flex flex-1 items-center gap-3 px-4 py-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="truncate text-sm font-bold text-slate-900 dark:text-white">{d.title}</div>
                              <Salad className="h-3.5 w-3.5 flex-none text-emerald-600/70 dark:text-emerald-300/80" />
                            </div>
                            <div className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{d.description}</div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {(d.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span key={`${d.id}-tag-${idx}`} className="rounded-full bg-blue-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="hidden flex-none flex-col items-end gap-1 sm:flex">
                            {d.waterTargetLiters !== undefined ? (
                              <div className="rounded-xl bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{d.waterTargetLiters} L</div>
                            ) : null}
                            {d.mealType ? (
                              <div className="rounded-xl bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{d.mealType}</div>
                            ) : null}
                          </div>

                          <div className="flex flex-none items-center gap-2">
                            <button
                              type="button"
                              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                              onClick={() => navigate(`/admin/numeroloji/diyet/${encodeURIComponent(d.id)}`)}
                            >
                              <PencilRuler className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50 dark:bg-slate-800 dark:hover:bg-rose-900/20"
                              disabled={dietDeleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu diyeti silmek istiyor musunuz?");
                                if (!ok) return;
                                dietDeleteMutation.mutate(d.id);
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={d.active}
                                disabled={dietSaveMutation.isPending}
                                onChange={() => dietSaveMutation.mutate({ ...d, active: !d.active })}
                              />
                              <div className="h-5 w-9 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-[16px] dark:bg-slate-700" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate("/admin/numeroloji/diyet/yeni")}
                    className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                  >
                    <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-xl">+</span>
                    </div>
                    <div className="text-sm font-bold">Yeni diyet ekle</div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
